// Pure PII scrubber for Sentry events — the SINGLE source of truth, shared by
// both the mobile SDK init (`beforeSend`, apps/mobile/src/lib/sentry.ts) and the
// edge runtime (supabase/functions/_shared/sentry.ts). Deliberately free of any
// Sentry / React Native / Deno import so it runs under any runtime (Metro, Deno,
// jsdom/vitest) and stays trivially testable. No internal imports either, so a
// Deno edge function can import this .ts file directly across the workspace.

export interface ScrubbableEvent {
  user?: { id?: string | number; [k: string]: unknown } | null;
  request?: {
    headers?: unknown;
    cookies?: unknown;
    data?: unknown;
    url?: unknown;
    query_string?: unknown;
    [k: string]: unknown;
  } | null;
  transaction?: unknown;
  message?: unknown;
  exception?: {
    values?: { value?: unknown; [k: string]: unknown }[];
    [k: string]: unknown;
  } | null;
  breadcrumbs?: { message?: unknown; data?: unknown; [k: string]: unknown }[] | null;
  extra?: unknown;
  contexts?: unknown;
  [k: string]: unknown;
}

// Free-text PII we redact wherever it appears in an event's strings. We can't
// enumerate every field a message might carry (a raw Postgres/Supabase error or a
// console breadcrumb can echo an email or token), so we pattern-redact instead of
// deleting — keeping errors debuggable while stripping the identifiers.
const EMAIL_RE = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi;
const BEARER_RE = /Bearer\s+[A-Za-z0-9._~+/-]+=*/gi;
const JWT_RE = /\beyJ[A-Za-z0-9._-]{10,}/g; // JWTs (access tokens) start with "eyJ"
// Sensitive URL query/fragment params, so an OPAQUE token (a PKCE `code`, a share
// token) that no other pattern catches is stripped when it rides in a URL — e.g.
// a recovery/magic-link URL captured in request.url or an error message.
const URL_TOKEN_RE =
  /([?&#](?:access_token|refresh_token|provider_token|code|token|apikey|api_key)=)[^&\s#]+/gi;
// The template-share token rides in a URL PATH segment (join/template/<token>), not a
// query param, so URL_TOKEN_RE misses it — and it's opaque (no email/JWT/Bearer shape).
// Redact the segment while keeping the route visible for debugging. `\b` before `join`
// anchors to a path boundary (`/join/…`, `--/join/…`, or string start) without matching
// e.g. `rejoin/`.
const SHARE_TOKEN_RE = /(\bjoin\/template\/)[^/?#\s]+/gi;

/** Redact emails, bearer credentials, JWTs, and URL-embedded tokens from a string. */
export function redactPII(value: string): string {
  return value
    .replace(BEARER_RE, 'Bearer [redacted]')
    .replace(JWT_RE, '[token]')
    .replace(URL_TOKEN_RE, '$1[redacted]')
    .replace(SHARE_TOKEN_RE, '$1[redacted]')
    .replace(EMAIL_RE, '[email]');
}

/**
 * A safe, redacted one-line summary of an error for `console.*` — device/edge
 * console logs never pass through `scrubEvent`. A Postgres/Supabase error can echo
 * row content (note bodies, step labels) in its `details`/`hint`, so log only the
 * name/code + a redacted message and drop everything else.
 */
export function redactError(err: unknown): string {
  if (err instanceof Error) return redactPII(`${err.name}: ${err.message}`);
  if (typeof err === 'string') return redactPII(err);
  const e = err as { code?: unknown; message?: unknown } | null;
  if (e && (e.code != null || e.message != null)) {
    const code = typeof e.code === 'string' ? e.code : '';
    const message = typeof e.message === 'string' ? redactPII(e.message) : '';
    return `${code} ${message}`.trim();
  }
  return '[redacted error]';
}

/**
 * Recursively redact PII from every string in a value (objects/arrays in place).
 * `seen` breaks reference cycles: this runs inside `beforeSend`, before Sentry's
 * own normalization, so the event can still carry a circular reference (e.g. a
 * cyclic object handed to `setExtra` or captured in a console breadcrumb). Without
 * the guard that would recurse until the stack blows and the event is lost.
 */
function deepRedact(value: unknown, seen = new WeakSet<object>()): unknown {
  if (typeof value === 'string') return redactPII(value);
  if (value && typeof value === 'object') {
    if (seen.has(value)) return value;
    seen.add(value);
    if (Array.isArray(value)) {
      for (let i = 0; i < value.length; i++) value[i] = deepRedact(value[i], seen);
      return value;
    }
    const record = value as Record<string, unknown>;
    for (const key of Object.keys(record)) record[key] = deepRedact(record[key], seen);
    return value;
  }
  return value;
}

/**
 * Strip data we never want leaving the device/server: reduce the user context to
 * just the id (drop email / ip_address), drop request headers (Authorization),
 * cookies, body, and raw query string, and redact emails / tokens (including
 * URL-embedded opaque tokens) from the free-text surfaces an error can smuggle
 * them through — the request URL, transaction name, exception messages, the
 * event message, breadcrumbs (which ride on the event into `beforeSend`), extra,
 * and contexts.
 */
export function scrubEvent<T extends ScrubbableEvent>(event: T): T {
  if (event.user) {
    event.user = event.user.id != null ? { id: event.user.id } : undefined;
  }
  if (event.request) {
    delete event.request.headers;
    delete event.request.cookies;
    delete event.request.data;
    // The URL/query can carry an opaque token (PKCE code, magic-link token);
    // drop the raw query string and redact the URL rather than delete it, so the
    // endpoint stays visible for debugging.
    delete event.request.query_string;
    if (typeof event.request.url === 'string') {
      event.request.url = redactPII(event.request.url);
    }
  }
  if (typeof event.transaction === 'string') {
    event.transaction = redactPII(event.transaction);
  }
  if (typeof event.message === 'string') {
    event.message = redactPII(event.message);
  }
  if (event.exception?.values) {
    for (const value of event.exception.values) {
      if (typeof value.value === 'string') value.value = redactPII(value.value);
    }
  }
  if (event.breadcrumbs) {
    for (const crumb of event.breadcrumbs) {
      if (typeof crumb.message === 'string') crumb.message = redactPII(crumb.message);
      if (crumb.data) crumb.data = deepRedact(crumb.data);
    }
  }
  if (event.extra) event.extra = deepRedact(event.extra);
  if (event.contexts) event.contexts = deepRedact(event.contexts);
  return event;
}
